import Konva from 'konva';
import { Node } from 'konva/lib/Node';
import { Vector2d } from 'konva/lib/types';
import { MutableRefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { getVertexEditShapeId } from '../../model/selectors/getVertexEditShapeId';
import { getSnapStepPixels } from '../../model/selectors/getSnapStepPixels';
import { Shape } from '../../model/types';

interface HookApi {
  layerRef: MutableRefObject<Nullable<Konva.Layer>>;
  stageRef: MutableRefObject<Nullable<Konva.Stage>>;
  transformerRef: MutableRefObject<Nullable<Konva.Transformer>>;
  resizeByGrid: (oldPos: Vector2d, newPos: Vector2d) => Vector2d;
}

export const useTransform = ({ shapes }: { shapes: Shape[] }): HookApi => {
  const layerRef = useRef<Konva.Layer>(null);
  const transformerRef = useRef<Konva.Transformer>(null);
  const stageRef = useRef<Konva.Stage>(null);

  const snapStepPixels = useAppSelector(getSnapStepPixels);
  const vertexEditShapeId = useAppSelector(getVertexEditShapeId);
  const selectedShapeIds = useMemo(() => shapes.filter((shape) => shape.selected).map((shape) => shape.id), [shapes]);
  const selectedShapeIdsKey = selectedShapeIds.join('|');

  useEffect(() => {
    if (!transformerRef.current) return;

    // While a polygon is in vertex-edit mode, hide the Transformer entirely.
    if (vertexEditShapeId) {
      transformerRef.current.nodes([]);
      transformerRef.current.getLayer()?.batchDraw();
      return;
    }

    const nodes = selectedShapeIds.reduce((acc: Node[], shapeId: string) => {
      const node = stageRef.current?.findOne(`#${shapeId}`);
      if (node) {
        const group = node.getType() === 'Group' ? node : node.findAncestor('.shape');
        acc.push(group || node);
      }
      return acc;
    }, []);

    transformerRef.current.nodes(nodes);
    transformerRef.current.getLayer()?.batchDraw();
    // Geometry changes should not rebind the Transformer; only selection-set
    // changes require a nodes() refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedShapeIdsKey, vertexEditShapeId]);

  const resizeByGrid = useCallback(
    (_oldPos: Vector2d, newPos: Vector2d): Vector2d => {
      if (transformerRef.current?.getActiveAnchor() === 'rotater') {
        return newPos;
      }

      return {
        x: Math.round(newPos.x / snapStepPixels) * snapStepPixels,
        y: Math.round(newPos.y / snapStepPixels) * snapStepPixels,
      };
    },
    [snapStepPixels],
  );

  return { layerRef, stageRef, transformerRef, resizeByGrid };
};
